import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/apiConfig';
import './DataChanges.css';

const DataChanges = () => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChange, setSelectedChange] = useState(null);
  const [filters, setFilters] = useState({
    tableName: '',
    action: '',
    startDate: '',
    endDate: '',
    limit: 100
  });
  

  useEffect(() => {
    fetchDataChanges();
  }, [filters]);

  const fetchDataChanges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await fetch(`${API_URL}/audit/data-changes?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch data changes');
      }
      
      const data = await response.json();
      setChanges(data.changes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'INSERT': return 'success';
      case 'UPDATE': return 'warning';
      case 'DELETE': return 'error';
      default: return 'default';
    }
  };

  const formatFieldChange = (field) => {
    if (!field) return null;
    return (
      <div className="field-change">
        <span className="field-name">{field.field}:</span>
        <span className="old-value">{JSON.stringify(field.oldValue)}</span>
        <span className="arrow">→</span>
        <span className="new-value">{JSON.stringify(field.newValue)}</span>
      </div>
    );
  };

  if (loading) {
    return <div className="changes-loading">Loading data changes...</div>;
  }

  if (error) {
    return <div className="changes-error">Error: {error}</div>;
  }

  return (
    <div className="data-changes-container">
      <div className="changes-filters">
        <select 
          value={filters.tableName} 
          onChange={(e) => setFilters({...filters, tableName: e.target.value})}
          className="filter-select"
        >
          <option value="">All Tables</option>
          <option value="employees">Employees</option>
          <option value="employee_documents">Documents</option>
          <option value="employee_onboard_forms">Forms</option>
          <option value="locations">Locations</option>
          <option value="paystubs">Paystubs</option>
        </select>
        
        <select 
          value={filters.action} 
          onChange={(e) => setFilters({...filters, action: e.target.value})}
          className="filter-select"
        >
          <option value="">All Actions</option>
          <option value="INSERT">Insert</option>
          <option value="UPDATE">Update</option>
          <option value="DELETE">Delete</option>
        </select>
        
        <input 
          type="datetime-local" 
          value={filters.startDate}
          onChange={(e) => setFilters({...filters, startDate: e.target.value})}
          className="filter-date"
          placeholder="Start Date"
        />
        
        <input 
          type="datetime-local" 
          value={filters.endDate}
          onChange={(e) => setFilters({...filters, endDate: e.target.value})}
          className="filter-date"
          placeholder="End Date"
        />
      </div>

      <div className="changes-table-container">
        <table className="changes-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Table</th>
              <th>Action</th>
              <th>Record ID</th>
              <th>User</th>
              <th>Changes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {changes.map(change => (
              <tr key={change.id}>
                <td className="time-cell">
                  {new Date(change.changedAt).toLocaleString()}
                </td>
                <td className="table-cell">{change.tableName}</td>
                <td>
                  <span className={`action-badge ${getActionColor(change.action)}`}>
                    {change.action}
                  </span>
                </td>
                <td className="record-id">{change.recordId}</td>
                <td>{change.username || change.firstName + ' ' + change.lastName || '-'}</td>
                <td className="changes-cell">
                  {change.changedFields && change.changedFields.length > 0 ? (
                    <span className="changes-count">
                      {change.changedFields.length} field(s) changed
                    </span>
                  ) : (
                    <span className="no-changes">-</span>
                  )}
                </td>
                <td>
                  <button 
                    onClick={() => setSelectedChange(change)}
                    className="view-details-btn"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {changes.length === 0 && (
        <div className="no-changes-message">No data changes found for the selected filters.</div>
      )}

      {selectedChange && (
        <div className="change-details-modal" onClick={() => setSelectedChange(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Data Change Details</h3>
            <span className="close" onClick={() => setSelectedChange(null)}>&times;</span>
            
            <div className="detail-section">
              <h4>Change Information</h4>
              <p><strong>Table:</strong> {selectedChange.tableName}</p>
              <p><strong>Record ID:</strong> {selectedChange.recordId}</p>
              <p><strong>Action:</strong> {selectedChange.action}</p>
              <p><strong>Time:</strong> {new Date(selectedChange.changedAt).toLocaleString()}</p>
              <p><strong>User:</strong> {selectedChange.username || '-'}</p>
              {selectedChange.changeReason && (
                <p><strong>Reason:</strong> {selectedChange.changeReason}</p>
              )}
            </div>

            {selectedChange.changedFields && selectedChange.changedFields.length > 0 && (
              <div className="detail-section">
                <h4>Field Changes</h4>
                <div className="field-changes-list">
                  {selectedChange.changedFields.map((field, index) => (
                    <div key={index} className="field-change-detail">
                      {formatFieldChange(field)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedChange.action === 'DELETE' && selectedChange.oldValues && (
              <div className="detail-section">
                <h4>Deleted Record</h4>
                <pre className="json-display">
                  {JSON.stringify(selectedChange.oldValues, null, 2)}
                </pre>
              </div>
            )}

            {selectedChange.action === 'INSERT' && selectedChange.newValues && (
              <div className="detail-section">
                <h4>Created Record</h4>
                <pre className="json-display">
                  {JSON.stringify(selectedChange.newValues, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataChanges;